import settings from '@nobia/zeus-components/lib/settings'
import styled, { theme } from '@nobia/zeus-components/lib/styled'
import rem from '@nobia/zeus-components/lib/styled/rem'
import PropTypes from 'prop-types'
import React from 'react'

import { margins } from '../../styles/margins'
import { useTranslation } from '../../utils/useTranslation'
import InformationContainer from '../informationContainer/InformationContainer'

const Confirmation = styled.div`
  padding: ${rem(22)} ${rem(17)};
  border-radius: 0 0 ${rem(6)} ${rem(6)};
  border: ${rem(1)} solid ${theme('forms.colors.border')};
  border-top: none;
  background-color: ${theme('colors.tone1')};
`
const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`
const Label = styled.label`
  opacity: 0.6;
  color: ${(p) => p.theme.colors.textColor};
  font-size: ${rem(15)};
  font-weight: ${(p) => p.theme.typography.paragraph.fontWeight};
  line-height: ${rem(22)};
`
const Value = styled.strong`
  margin: 0;
  margin-bottom: ${rem(14)};
  font-weight: 500;
  line-height: ${rem(22)};

  color: ${(p) => p.theme.colors.textColor};
  font-size: ${rem(15)};
  font-style: normal;
`
const Address = styled(Value)`
  margin-bottom: 0;
`
const Row = styled.div`
  display: flex;
  justify-content: space-between;
`
const Edit = styled.button`
  border: none;
  background-color: inherit;
  color: ${theme('colors.primary')};
  font-size: ${rem(15)};
  font-weight: ${(p) => p.theme.typography.paragraph.fontWeight};
  letter-spacing: normal;
  text-decoration: underline;
  cursor: pointer;
`
const P = styled.p`
  color: ${(p) => p.theme.colors.textColor};
  font-size: ${rem(15)};
  font-weight: 500;
  line-height: ${rem(22)};
  margin: 0 0 ${rem(12)} 0;
`
const Constraint = styled.div`
  max-width: ${rem(200)};
  margin: 0 auto;
  & > ${P} {
    text-align: center;
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: space-around;
`
const Button = styled.button`
  font-size: ${(p) => rem(p.theme.buttons.fontSize.small)};
  font-weight: ${(p) => p.theme.buttons.fontWeight};
  line-height: ${rem(22)};
  text-align: center;
  color: ${theme('buttons.primary.color')};
  border-radius: ${(p) => rem(p.theme.buttons.borderRadius)};
  background-color: ${theme('buttons.primary.background')};
  padding: ${rem(5)} ${rem(margins.m)};
  text-transform: uppercase;

  &:hover {
    background-color: ${theme('buttons.primary.activeBackground')};
  }
`

const ContactInfo = styled.div`
  background-color: #dfc6b333;
  font-family: Campton;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  padding: 18px 20px;
  width: calc(100% + 40px);
  margin: 0 -20px -21px;
`

const UserDetails = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  city,
  postalCode,
  houseNumber,
  addressLine1,
  addressLine2,
  addressLine3,
  customerNumber,
  detailsAreCorrect,
  onEditCustomer,
  onDetailsCorrect,
}) => {
  const { t } = useTranslation('app.pages.user-settings.details')
  const addresses = [
    `${houseNumber || ''} ${addressLine1 || ''}`,
    addressLine2,
    addressLine3,
  ]
    .filter(Boolean)
    .map((f) => f.trim())

  const isMagnet = settings.brand === 'magnet'

  return (
    <>
      <InformationContainer
        title={t('heading')}
        borderRadius={detailsAreCorrect ? '6px' : '6px 6px 0 0'}
      >
        <Column>
          <Row>
            <Label>{t('name')}</Label>
            {!isMagnet && <Edit onClick={onEditCustomer}>{t('edit')}</Edit>}
          </Row>
          <Value data-cs-mask>
            {firstName} {lastName}
          </Value>
        </Column>
        {customerNumber ? (
          <Column>
            <Label>{t('customerNumber')}</Label>
            <Value>{customerNumber}</Value>
          </Column>
        ) : null}
        <Column>
          <Label>{t('phone')}</Label>
          <Value data-cs-mask>{phoneNumber}</Value>
        </Column>
        <Column>
          <Label>{t('address')}</Label>
          {addresses.map((address) => (
            <Address key={address} data-cs-mask>
              {address}
            </Address>
          ))}
          <Address data-cs-mask>{city}</Address>
          <Value data-cs-mask>{postalCode}</Value>
        </Column>
        <Column>
          <Label>{t('email')}</Label>
          <Value data-cs-mask>{email}</Value>
        </Column>
        {isMagnet && <ContactInfo>{t('contactInfo')}</ContactInfo>}
      </InformationContainer>
      {!isMagnet && !detailsAreCorrect && (
        <Confirmation>
          <Constraint>
            <P>{t('confirmation.title')}</P>
            <Buttons>
              <Button onClick={() => onDetailsCorrect(true)}>
                {t('confirmation.yes')}
              </Button>
              <Button onClick={() => onDetailsCorrect(false)}>
                {t('confirmation.no')}
              </Button>
            </Buttons>
          </Constraint>
        </Confirmation>
      )}
    </>
  )
}

UserDetails.defaultProps = {
  email: '',
  firstName: '',
  lastName: '',
  customerNumber: '',
  phoneNumber: '',
  postalCode: '',
  city: '',
  houseNumber: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  detailsAreCorrect: false,
  onEditCustomer: () => {},
  onDetailsCorrect: () => {},
}

UserDetails.propTypes = {
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  customerNumber: PropTypes.string,
  phoneNumber: PropTypes.string,
  postalCode: PropTypes.string,
  city: PropTypes.string,
  houseNumber: PropTypes.string,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  addressLine3: PropTypes.string,
  onEditCustomer: PropTypes.func,
  detailsAreCorrect: PropTypes.bool,
  onDetailsCorrect: PropTypes.func,
}

export default UserDetails
